/**
 * Renders a markdown source string using the in-house mini-parser.
 *
 * Wraps the parsed output in a `.markdownContent` div so the Henaket
 * typography rules in MarkdownContent.module.css can target it via
 * descendant selectors. Without the wrapper, our h1/h2/p/li styles
 * would leak everywhere they're not wanted.
 *
 * 0.36.61 — Optional `color` prop. When set (CSS variable name like
 * 'var(--color-blue-800)' or a hex string), every text element inside
 * the markdown block uses that color. Implemented via the
 * `--md-text-color` CSS custom property: the wrapper div sets it
 * inline, and every typography rule in MarkdownContent.module.css
 * references it as `var(--md-text-color, var(--color-text-800))` —
 * so without an override, the default Henaket text color applies.
 *
 * 0.36.62 — Optional `size` prop. Named scale step that proportionally
 * sizes EVERY element in the block (headings, paragraphs, lists, code
 * blocks). Implemented via the `--md-scale` CSS custom property: all
 * font-size and line-height values in the CSS read
 * `calc(<base>px * var(--md-scale, 1))`, so scale=1 is "normal" and
 * the multiplier scales everything in proportion. Supported steps:
 *   'xs'   → 0.8125  (body ~13px)
 *   'sm'   → 0.875   (body ~14px)
 *   'base' → 1.0     (body 16px, default)
 *   'lg'   → 1.25    (body ~20px)
 *   'xl'   → 1.5     (body ~24px)
 * Unknown size strings fall back to scale 1.
 *
 * 0.36.65 — `marginBottom` prop REMOVED. Bottom-margin spacing is
 * now applied at the COL WRAPPER level by PageContentRenderer and
 * AdminFormPreview, driven by `element.margin_bottom`. This avoids
 * the stacking bug where MarkdownContent's wrapper added its own
 * margin INSIDE the col, but the col itself ALSO had a hardcoded
 * margin — total visible space below the block was wrapper-margin
 * PLUS col-margin (e.g., 4 + 16 = 20px). Putting it at the col
 * level — the layout container — is the architecturally right
 * place for it. Standalone MarkdownContent callers (FaqAccordion,
 * AdminStaticPages preview) don't need bottom margin since they're
 * already inside a container that handles spacing.
 */
import { parseMarkdown } from '../lib/markdown.jsx';
import styles from './MarkdownContent.module.css';

const SIZE_SCALES = {
  xs: 0.8125,
  sm: 0.875,
  base: 1,
  lg: 1.25,
  xl: 1.5,
};

export default function MarkdownContent({ source, color, size, leading }) {
  const elements = parseMarkdown(source);
  if (elements.length === 0) return null;
  const scale = SIZE_SCALES[size];  // undefined if size unset/unknown
  const styleObj = {};
  if (color) styleObj['--md-text-color'] = color;
  if (scale && scale !== 1) styleObj['--md-scale'] = scale;
  // 0.45.08 - leading: px offset from Henaket's line heights
  // (negative = tighter). 0 or unset = Henaket.
  if (Number(leading)) styleObj['--text-leading-step'] = `${-Number(leading)}px`;
  const style = Object.keys(styleObj).length > 0 ? styleObj : undefined;
  return <div className={styles.markdownContent} style={style}>{elements}</div>;
}
